// OBJECT REGISTRY — every fridge object the site knows about.
// All objects are hand-drawn cartoon SVGs — one visual style across the whole site.
// name  : the label that pops up on hover
// sound : material-true synthesized sound on hover/tap (see src/lib/sounds.js)
//         steel clanks, glass pings, plastic pops, packets crinkle, greens rustle.
// anim  : cartoon reaction on hover (wobble | squish | bounce | shake | roll)
// still : true = pure decoration, not interactive at all
import * as F from "./freezerObjects";
import * as M from "./fridgeObjects";
import * as D from "./drawerObjects";

export const REGISTRY = {
  // freezer — hard & frozen: ice clinks, packets rattle, tubs pop
  "ice-cream": { Comp: F.IceCream, name: "ice cream", sound: "pop", anim: "wobble" },
  "frozen-fish": { Comp: F.FrozenFish, name: "frozen fish", sound: "thud", anim: "wobble" },
  "kulfi": { Comp: F.Kulfi, name: "kulfi", sound: "clink", anim: "wobble" },
  "popsicle": { Comp: F.Popsicle, name: "popsicle", sound: "clink", anim: "bounce" },
  "peas-packet": { Comp: F.PeasPacket, name: "frozen peas", sound: "rattle", anim: "shake" },
  "pizza-box": { Comp: F.PizzaBox, name: "pizza box", sound: "thump", anim: "wobble" },
  "ice-cube-tray": { Comp: F.IceCubeTray, name: "ice cube tray", sound: "clink", anim: "shake" },

  // main fridge — glass jars ping, steel clanks, soft food squishes
  "achaar-jar": { Comp: M.AchaarJar, name: "achaar", sound: "glass", anim: "wobble" },
  "steel-dabba": { Comp: M.SteelDabba, name: "steel dabba", sound: "clank", anim: "shake" },
  "tiffin-box": { Comp: M.TiffinBox, name: "tiffin", sound: "clank", anim: "bounce" },
  "sandwich": { Comp: M.Sandwich, name: "overloaded sandwich", sound: "squish", anim: "squish" },
  "cheese-wheel": { Comp: M.CheeseWheel, name: "wheel of cheese", sound: "thud", anim: "roll" },
  "roast-chicken": { Comp: M.RoastChicken, name: "roast chicken", sound: "squish", anim: "bounce" },
  "layer-dessert": { Comp: M.LayerDessert, name: "layered dessert", sound: "glass", anim: "wobble" },
  "milk-packet": { Comp: M.MilkPacket, name: "milk", sound: "crinkle", anim: "wobble" },
  "curd-container": { Comp: M.CurdContainer, name: "curd", sound: "pop", anim: "wobble" },
  "coriander-bowl": { Comp: M.CorianderBowl, name: "dhaniya bowl", sound: "clank", anim: "bounce" },
  "paneer-cube": { Comp: M.PaneerCube, name: "paneer", sound: "squish", anim: "wobble" },
  "leftover-container": { Comp: M.LeftoverContainer, name: "leftovers", sound: "clank", anim: "wobble" },
  "peanut-butter": { Comp: M.PeanutButterJar, name: "peanut butter", sound: "glass", anim: "wobble" },
  "water-bottle": { Comp: M.WaterBottle, name: "water bottle", sound: "plop", anim: "wobble" },
  "sauce-bottle": { Comp: M.SauceBottle, name: "sauce", sound: "squish", anim: "squish" },
  "chocolate-cake": { Comp: M.ChocolateCake, name: "chocolate cake", sound: "squish", anim: "wobble" },
  "candy-jar": { Comp: M.CandyJar, name: "candy jar", sound: "rattle", anim: "shake" },
  "soda-can": { Comp: M.SodaCan, name: "soda", sound: "clank", anim: "roll" },
  "soda-bottle": { Comp: M.SodaBottle, name: "cold drink", sound: "plop", anim: "wobble" },
  "juice-carton": { Comp: M.JuiceCarton, name: "juice", sound: "crinkle", anim: "wobble" },
  "milk-carton": { Comp: M.MilkCarton, name: "milk carton", sound: "plop", anim: "wobble" },
  "egg-tray": { Comp: M.EggTray, name: "eggs", sound: "clink", anim: "shake" },
  "butter-tub": { Comp: M.ButterTub, name: "butter", sound: "pop", anim: "wobble" },
  "pastry": { Comp: M.Pastry, name: "donut", sound: "squish", anim: "bounce" },
  "watermelon-slice": { Comp: M.WatermelonSlice, name: "watermelon", sound: "squish", anim: "wobble" },
  // spills & mess — pure decoration, fridge chaos
  "ketchup-spill": { Comp: M.KetchupSpill, name: "oops.", still: true },
  "mustard-spill": { Comp: M.MustardSpill, name: "oops.", still: true },
  "milk-spill": { Comp: M.MilkSpill, name: "no use crying.", still: true },

  // drawer — firm produce thuds & rolls, leafy greens rustle
  "apple": { Comp: D.Apple, name: "apple", sound: "thud", anim: "bounce" },
  "lemon": { Comp: D.Lemon, name: "lemon", sound: "thud", anim: "roll" },
  "tomato": { Comp: D.Tomato, name: "tomato", sound: "squish", anim: "squish" },
  "chilli": { Comp: D.Chilli, name: "green chilli", sound: "rustle", anim: "shake" },
  "carrot": { Comp: D.Carrot, name: "carrot", sound: "thump", anim: "roll" },
  "orange": { Comp: D.Orange, name: "orange", sound: "thud", anim: "roll" },
  "onion": { Comp: D.Onion, name: "onion", sound: "rustle", anim: "wobble" },
  "potato": { Comp: D.Potato, name: "potato", sound: "thud", anim: "roll" },
  "broccoli": { Comp: D.Broccoli, name: "broccoli", sound: "rustle", anim: "wobble" },
  "coriander-bunch": { Comp: D.CorianderBunch, name: "dhaniya", sound: "rustle", anim: "shake" },
  "bananas": { Comp: D.Bananas, name: "bananas", sound: "thud", anim: "wobble" },
  "lettuce": { Comp: D.Lettuce, name: "lettuce", sound: "rustle", anim: "wobble" },
  "capsicum": { Comp: D.Capsicum, name: "capsicum", sound: "thud", anim: "bounce" },
};
